import { Alert, Box, Button, Container, FormField, Header, Input, SpaceBetween } from "@cloudscape-design/components";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router";
import AwsLoginModal from "../modals/AwsLoginModal";
import { isEmpty, regExpAll } from "./utils.js";
import { fetchAwsLoginUrl, getEndUserEvent } from "../../redux/actions/aws_login";

const EventLogin = () => {
    const { urlParamEventId } = useParams();
    const [value, setValue] = useState("");
    const [inputError, setInputError] = useState({});
    const [valueChangedOnce, setValueChangedOnce] = useState(false);
    const NotificationItem = useSelector((state) => state.notification);
    const User = useSelector((state) => state.current_user);
    const Config = useSelector((state) => state.config);
    const AwsLogin = useSelector((state) => state.aws_login);
    const dispatch = useDispatch();

    const clearEvent = () => {
        dispatch({ type: "aws_login/event_dismiss" });
        dispatch({ type: "notification/dismiss" });
        setInputError({});
        setValueChangedOnce(true);
    };

    const validateInputs = (newValue, allowEmptyValues = false) => {
        let errors = {};
        if (!(allowEmptyValues && newValue === "") && !new RegExp(regExpAll(Config.EVENT_ID_REGEX)).test(newValue)) {
            errors.EVENT_ID =
                "Invalid event ID format. Event ID must consist of " +
                Config.EVENT_ID_LENGTH +
                " alphanumeric characters.";
        }
        setInputError(errors);
        return isEmpty(errors);
    };

    const submit = () => {
        dispatch({ type: "notification/dismiss" });
        if (!validateInputs(value)) {
            return;
        }
        dispatch(getEndUserEvent(value));
    };

    const updateFormValue = (update) => {
        validateInputs(update, true);
        setValue(update);
    };

    useEffect(() => {
        if (!valueChangedOnce && isEmpty(AwsLogin.event) && urlParamEventId && User.isLoggedIn) {
            dispatch(getEndUserEvent(urlParamEventId));
        }
    }, [AwsLogin.event, urlParamEventId, dispatch, valueChangedOnce, User]);

    useEffect(() => {
        if (!AwsLogin.event.eventStatus) return;
        switch (AwsLogin.event.eventStatus) {
            case "Waiting":
                dispatch({
                    type: "notification/waiting",
                    message:
                        "This event hasn't been started yet. Please wait until your event operator allows you to log into your AWS account and then refresh this browser page."
                });
                return;
            case "Running":
                return;
            case "Terminated":
                dispatch({
                    type: "notification/terminated",
                    message: "This event has already been terminated by your event operator."
                });
                return;
            default:
                dispatch({
                    type: "notification/error",
                    message: "Internal event status error. Please ask your event support for help."
                });
        }
    }, [AwsLogin.event, dispatch]);

    useEffect(() => {
        dispatch({ type: "aws_login/event_dismiss" });
        dispatch({ type: "notification/dismiss" });
    }, [dispatch]);

    /////////
    // MAIN
    /////////

    return (
        <Container>
            <SpaceBetween size="xl">
                {!AwsLogin.event.id ? (
                    <SpaceBetween size="m">
                        <FormField
                            description="Ask your event support staff for details"
                            errorText={inputError.EVENT_ID}
                            label="Enter the event id for today's event:"
                        >
                            <Input value={value} onChange={({ detail }) => updateFormValue(detail.value)} />
                        </FormField>
                        <Button variant="primary" onClick={submit} loading={AwsLogin.event_status === "loading"}>
                            Log in to event
                        </Button>
                    </SpaceBetween>
                ) : (
                    <>
                        <Header variant="h1" actions={<Button onClick={clearEvent}>Change event</Button>}>
                            Welcome to "{AwsLogin.event.eventName}"
                        </Header>
                        {User.isOperator || User.isAdmin ? (
                            <Alert type="warning" header="IMPORTANT for operators & admins">
                                Be careful: Clicking on the button below will consume an AWS account from the
                                account pool list.
                            </Alert>
                        ) : null}
                        <Alert type="info" header="Account Leasing Information">
                            <Box style={{ fontSize: "16px", fontWeight: "bold", color: "#0050b3", backgroundColor: "#e6f7ff", padding: "10px", borderRadius: "5px" }}>
                                All AWS account leases are <strong>free of charge</strong>, but they are limited to a maximum duration of <strong>24 hours</strong>. 
                                After this period, the account will be automatically cleaned, and all resources will be destroyed.
                            </Box>
                        </Alert>
                        <Alert type="warning" header="IMPORTANT for users">
                            <Box style={{ fontSize: "16px", color: "#856404", backgroundColor: "#fff3cd", padding: "15px", borderRadius: "8px", border: "1px solid #ffeeba" }}>
                                <p style={{ fontWeight: "bold", marginBottom: "10px" }}>
                                    To ensure cost control and efficient use of resources in the AllianzTech playground, please follow these rules:
                                </p>
                                <ul style={{ paddingLeft: "20px", margin: "0" }}>
                                    <li style={{ marginBottom: "8px" }}>
                                        <strong>Supported Regions:</strong> Only <code>us-east-1</code> and <code>us-west-2</code> are available.
                                    </li>
                                    <li style={{ marginBottom: "8px" }}>
                                        <strong>Instance Types:</strong> Only <code>t2.nano</code>, <code>t2.micro</code>, <code>t2.small</code>, and <code>t2.medium</code> are allowed.
                                    </li>
                                    <li style={{ marginBottom: "8px" }}>
                                        <strong>Lease Duration:</strong> Each account lease is limited to <strong>24h</strong> from the time of request.
                                    </li>
                                    <li style={{ marginBottom: "8px" }}>
                                        <strong>Automatic Cleanup:</strong> After the lease ends, all resources will be cleaned up, and the account will be reset.
                                    </li>
                                    <li style={{ marginBottom: "8px" }}>
                                        <strong>Cost Limit:</strong> While account leasing is <strong>free of charge</strong> for users, each account is subject to a maximum budget of <strong>50 EUR</strong> provided by AllianzTech. If the account exceeds this limit, it will be automatically disabled to prevent additional costs.
                                    </li>
                                </ul>
                            </Box>
                        </Alert>
                        <Alert type="warning" header="Restricted services">
                            <Box style={{ fontSize: "16px", color: "#856404", backgroundColor: "#fff3cd", padding: "15px", borderRadius: "8px", border: "1px solid #ffeeba" }}>
                                <ul style={{ paddingLeft: "20px", margin: "0" }}>
                                    <li style={{ marginBottom: "8px" }}>
                                        <strong>The following services are currently restricted:</strong> SageMaker and OpenSearch Service.
                                    </li>
                                </ul>
                            </Box>
                        </Alert>
                        <Alert type="info" header="AI Models in AWS Bedrock">
                            <Box style={{ fontSize: "16px", color: "#0050b3", backgroundColor: "#e6f7ff", padding: "15px", borderRadius: "8px", border: "1px solid #91d5ff" }}>
                                <p style={{ fontWeight: "bold", marginBottom: "10px" }}>
                                    By default, the following 8 AI models are enabled in AWS Bedrock:
                                </p>
                                <ul style={{ paddingLeft: "20px", margin: "0" }}>
                                    <li style={{ marginBottom: "8px" }}>Claude 4 Sonnet: Advanced model with superior multilingual, coding, and vision capabilities for efficient complex task handling.</li>
                                    <li style={{ marginBottom: "8px" }}>Claude 3.5 Sonnet v2: Fast mid-tier model with strong multilingual, coding, and vision capabilities (interpreting charts, etc.)</li>
                                    <li style={{ marginBottom: "8px" }}>Claude 3.7 Sonnet: Hybrid reasoning model combining rapid replies and deep step-by-step thinking, great for complex tasks</li>
                                    <li style={{ marginBottom: "8px" }}>Claude 3.5 Haiku: Ultra-low-latency model designed for real-time chatbots and fast tool use</li>
                                    <li style={{ marginBottom: "8px" }}>Mistral Large (24.02): Open-source model with top-tier reasoning and code-generation capabilities</li>
                                    <li style={{ marginBottom: "8px" }}>Mistral Small (24.02): Open-source, lightweight 24B-parameter model optimized for cost-efficient inference</li>
                                    <li style={{ marginBottom: "8px" }}>Pixtral Large (25.02): Multimodal with a massive 128K context window, strong in vision + text tasks</li>
                                    <li style={{ marginBottom: "8px" }}>deepseek.r1-v1:0: Open-source, budget-friendly reasoning model rivaling top commercial models</li>
                                </ul>
                                <p style={{ marginTop: "10px" }}>
                                    Please use our Sandbox Helper: <strong><a href="https://github.developer.allianz.io/genai/sandbox-helper" target="_blank">https://github.developer.allianz.io/genai/sandbox-helper</a></strong>.
                                </p>
                                <p style={{ marginTop: "10px" }}>
                                    If you want to try any other models, feel free to request access using a <strong>local IAM user</strong> (not the federated user).
                                </p>
                            </Box>
                        </Alert>
                        <Box>
                            Click the button below to be forwarded to your AWS account. If this is the first time you
                            are using your email address to log into an AWS account for this event, we will assign a
                            new, fresh AWS account to you. If you have already been logging into an AWS account with
                            your email address before, we will log you into the same account again so you can continue
                            your work.
                        </Box>
                        <Button
                            icon="external"
                            variant="primary"
                            loading={AwsLogin.status === "loading"}
                            onClick={() => dispatch(fetchAwsLoginUrl({eventId: AwsLogin.event.id}))}
                            disabled={NotificationItem.visible}
                        >
                            Open AWS Console
                        </Button>
                        <AwsLoginModal />
                    </>
                )}
                <Alert
                    type={NotificationItem.type}
                    header={NotificationItem.header}
                    onDismiss={() => dispatch({ type: "notification/dismiss" })}
                    visible={NotificationItem.visible}
                    dismissAriaLabel="Close error"
                    dismissible
                >
                    {NotificationItem.content}
                </Alert>
            </SpaceBetween>
        </Container>
    );
};

export default EventLogin;
